import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  approveQcDetails,
  getQcDetails,
  getQcAdditionalData,
  resetState,
  saveQcDetails,
  setQcDetails,
} from "../../store/actions/QcDetails";
import variables from "../../constants/assets/variables";
import { getTabsConfig } from "./configs/tabs.config";

import { MainPageWrapper } from "./QcFieldIdInput";
import QcDriverDocumentDetailFields from "../../constants/QcDetails/QcDocumentDetailFields.json";

import { useHistory, useParams } from "react-router-dom";
import "./index.css";
import Tabs from "../../components/QcDashboard/UI/Tabs";
import Input from "../../components/QcDashboard/UI/Input/index";
import { Button } from "../../components/QcDashboard/styles/commonStyles";
import ScreenLoader from "../../components/QcDashboard/UI/ScreenLoader";
import MenuNames from "../../constants/Menu/MenuNames.json";
import MenuActionsMappings from "../../constants/Menu/MenuActionsMappings";
import { capitalizeFirstLetter, getPath, isEmpty } from "../../utils";

import {
  handleQcValidations,
  shouldQcApproved,
} from "../../components/QcDetails/HelperFunctions";
import { handlePhotoVerificationValidations } from "../../components/QcDashboard/utils/validators";
import ErrorModal from "../../components/Common/ErrorModal";
import {
  isAllApproved,
  isAnyStepRejected,
} from "../../components/QcDashboard/utils/helper";
import StatusBanner from "../../components/QcDashboard/UI/StatusBanner";
import Permission from "../../components/Common/Permission";
import { PHOTO_VERIFICATION_STEPS } from "../../components/QcDashboard/views/PhotoVerification/config/photoVerificationSteps";

var interval = null;

const qcWriteRole = MenuActionsMappings[MenuNames.QUALITY_CHECK_V2].WRITE;

const QcDetails = ({
  getQcDetails,
  getQcAdditionalData,
  approveQcDetails,
  saveQcDetails,
  qcDetails,
  currentActiveFeId,
  qcRejectionReasons,
  qcDocTypes,
  setQcDetails,
  resetState,
}) => {
  const params = useParams();
  const history = useHistory();
  const [currentTab, setCurrentTab] = useState();
  const [feId, setFeId] = useState(params.feId);
  const [initialState, setInitialState] = useState({});
  const [editableField, setEditableField] = useState("");
  const [fetchingQcDetails, setFetchingQcDetails] = useState(true);
  const [photoVerificationErrors, setPhotoVerificationErrors] = useState({});
  const [isFinalApprovalModalOpen, setIsFinalApprovalModalOpen] =
    useState(false);

  const [errors, setErrors] = useState([]);

  const photoVerificationSteps = Object.keys(qcDetails?.basic_details || {})
    .filter(
      (key) =>
        PHOTO_VERIFICATION_STEPS.findIndex((step) => step.key === key) > -1
    )
    .map((key) => ({ key: key }));

  const editStateError = `${capitalizeFirstLetter(
    editableField.split("_").join(" ")
  )} field is in edit state, please save or cancel changes before moving forward`;

  const [showSingleFieldEditOptions, setShowSingleFieldEditOptions] =
    useState("");
  const [showQcButton, setShowQcButton] = useState(false);
  const [qcApproved, setQcApproved] = useState(false);
  const allActions = MenuActionsMappings[MenuNames.QUALITY_CHECK];
  const onboardingFlow = getPath(qcDetails, "onboarding_flow", "");

  const showField = (field) => {
    if (field === "bank_doc") {
      return getPath(initialState, "document_details.bank_doc.display", true);
    }
    return true;
  };

  const getRequiredFlag = (field) => {
    return getPath(qcDetails, field.valuePath, "")["required"];
  };

  const sortDocuments = (obj) => {
    var accumulator = Object.keys(obj)
      .sort()
      .reduce((accumulator, key) => {
        accumulator[key] = obj[key];

        return accumulator;
      }, {});
    return accumulator;
  };

  const documentDetailsSteps = useMemo(() => {
    return Object.keys(sortDocuments(initialState?.document_details || {}))
      .filter(
        (field) =>
          getPath(Object.keys(qcDetails), "length", 0) > 0 &&
          QcDriverDocumentDetailFields[field] &&
          getRequiredFlag(QcDriverDocumentDetailFields[field]) &&
          showField(field)
      )
      .map((val) => ({
        key: val,
      }));
  }, [initialState]);

  const areAllPhotoVerificationStepsApproved = isAllApproved(
    initialState,
    photoVerificationSteps
  );

  const areAllDocumentVerificationStepsApproved = isAllApproved(
    initialState,
    documentDetailsSteps,
    "document_details"
  );

  const anyStepsRejected = isAnyStepRejected(
    qcDetails,
    documentDetailsSteps,
    "document_details"
  );

  const isTerminated = qcDetails?.driver_lifecycle_status === "terminated";
  const isApproved = qcDetails?.qc_status === "APPROVED";

  const areAllStepsApproved =
    areAllPhotoVerificationStepsApproved &&
    areAllDocumentVerificationStepsApproved;

  //custom debouncing implemented
  const getDriverQcDetails = (feId) => {
    clearTimeout(interval);
    interval = setTimeout(() => {
      setFetchingQcDetails(true);
      resetState({ state: "qcDetails" });
      resetState({ state: "currentActiveFeId" });
      getQcDetails(feId)
        .then((res) => {
          if (res) {
            window.history.replaceState(null, null, `/qc-details-v2/${feId}`);
            setEditableField("");
            setShowSingleFieldEditOptions("");
          }
        })
        .finally(() => {
          setFetchingQcDetails(false);
        });
    }, 500);
  };

  const IsQcStatusApproved = () => {
    setQcApproved(getPath(qcDetails, "qc_status", "") === "APPROVED");
  };

  useEffect(() => {
    const payload = {
      basic_details: qcDetails.basic_details
        ? JSON.parse(JSON.stringify(qcDetails.basic_details))
        : {},
      document_details: qcDetails.document_details
        ? JSON.parse(JSON.stringify(qcDetails.document_details))
        : {},
    };
    if (editableField == "") {
      setInitialState(payload);
    }

    if (qcDetails && Object.keys(qcDetails).length > 0) {
      if (!qcApproved && shouldQcApproved(payload)) {
        setShowQcButton(true);
      } else {
        setShowQcButton(false);
      }
      IsQcStatusApproved();
    }
  }, [qcDetails]);

  useEffect(() => {
    getQcAdditionalData();
  }, []);

  useEffect(() => {
    if (feId) {
      getDriverQcDetails(feId);
    }
  }, [feId]);

  const handleVerifiedChange = (item, val, inKey) => {
    const values = { ...initialState };
    val = val === "true" ? true : false;
    if (val) {
      values[inKey][item.key]["rejection_reason"] = "";
    }
    values[inKey][item.key]["verified"] = val;
    if (!qcApproved && shouldQcApproved(values)) {
      setShowQcButton(true);
    } else {
      setShowQcButton(false);
    }
    setInitialState(values);
  };

  const handleRejectionReasonChange = (keyToChange, val, extraParams) => {
    const item = extraParams[0];
    const inKey = extraParams[1];
    const values = { ...initialState };
    values[inKey][item.key]["rejection_reason"] = val;
    setInitialState(values);
  };

  const handleDocTypeChange = (keyToChange, val, extraParams) => {
    const item = extraParams[0];
    const inKey = extraParams[1];
    const values = { ...initialState };
    values[inKey][item.key]["doc_type"] = val;
    setInitialState(values);
  };

  const verifiedOptions = [
    {
      label: "Accept",
      value: true,
      disabled: qcApproved,
    },
    {
      label: "Reject",
      value: false,
      disabled: qcApproved,
    },
  ];

  const handleSaveClick = () => {
    if (editableField !== "") {
      setErrors([editStateError]);
      return;
    }

    if (currentTab?.path === "1") {
      const errors = handlePhotoVerificationValidations(
        initialState,
        "qc_details_container"
      );

      if (isEmpty(errors)) {
        setPhotoVerificationErrors({});
      } else {
        setPhotoVerificationErrors(errors);
        return;
      }
    }

    const validationErrors = handleQcValidations(initialState, qcDetails);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }
    saveQcDetails(feId, initialState).then((res) => {
      if (res) {
        setEditableField("");
        setShowSingleFieldEditOptions("");
        getQcDetails(feId);
      }
    });
  };

  const handleFeIdInputKeyDown = (e) => {
    if (e.key === "Enter" && feId) {
      history.push(`/qc-details-v2/${feId}`);
    }
  };

  const getPhotoVerificationError = (key) => {
    return photoVerificationErrors[key];
  };

  const setPhotoVerificationError = (key, value) => {
    setPhotoVerificationErrors({ ...photoVerificationErrors, [key]: value });
  };

  const removePhotoVerificationError = (key) => {
    const newErrors = { ...photoVerificationErrors };
    delete newErrors[key];
    setPhotoVerificationErrors(newErrors);
  };

  const handleErrorModalSubmit = () => {
    setErrors([]);
  };

  const handleQcApprove = () => {
    // const payload = {
    //   basic_details: qcDetails.basic_details,
    //   document_details: qcDetails.document_details,
    // };
    // const validationErrors = handleQcValidations(payload);
    const validationErrors = handleQcValidations(initialState, qcDetails);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }
    saveQcDetails(feId, initialState).then((res) => {
      if (res) {
        setEditableField("");
        setShowSingleFieldEditOptions("");
        // const newDetails = JSON.parse(JSON.stringify(qcDetails));
        // newDetails["basic_details"] = initialState.basic_details;
        // newDetails["document_details"] = initialState.document_details;
        let aprovePayload = { qc_status: "APPROVED" };
        if (!["PARTIALLY_APPROVED", "REJECTED"].includes(qcDetails.qc_status)) {
          aprovePayload = { qc_status: "PARTIALLY_APPROVED" };
        }
        if (qcDetails.blinkit_revamped_flow) {
          aprovePayload = { qc_status: "APPROVED" };
        }
        setTimeout(() => {
          approveQcDetails(feId, aprovePayload).then(() => {
            history.push(`/qc-details-v2/${feId}`);
          });
        }, 500);
      }
    });
  };

  return [
    <MainPageWrapper id="qc_details_container" className="font-lexend">
      <Navigation>
        <Tabs
          tabs={getTabsConfig()}
          currentTab={currentTab}
          setCurrentTab={setCurrentTab}
        />

        <Input
          className="fe-id-input"
          value={feId}
          onChange={(e) => setFeId(e.target.value)}
          onKeyDown={handleFeIdInputKeyDown}
          placeholder="Enter FE ID here"
        />
        <Permission requiredPerms={[qcWriteRole]}>
          {areAllStepsApproved && !fetchingQcDetails ? (
            <Button
              onClick={handleQcApprove}
              disabled={isTerminated || fetchingQcDetails || isApproved}
            >
              Qc Approve & Proceed
            </Button>
          ) : (
            <Button
              width={180}
              onClick={handleSaveClick}
              disabled={isTerminated || fetchingQcDetails || isApproved}
            >
              Save
            </Button>
          )}
        </Permission>
      </Navigation>

      {isTerminated ? (
        <StatusBanner
          style={{ marginTop: "20px" }}
          type="error"
          title="Driver is terminated"
          message="You will not be able to make changes"
        />
      ) : (
        anyStepsRejected &&
        currentTab?.path === "2" && (
          <StatusBanner
            style={{ marginTop: "20px" }}
            type="error"
            title="Document verification failed"
          />
        )
      )}

      {fetchingQcDetails ? (
        <ScreenLoader />
      ) : (
        <currentTab.component
          setCurrentTab={setCurrentTab}
          onboardingFlow={onboardingFlow}
          editableField={editableField}
          initialState={initialState}
          setInitialState={setInitialState}
          details={qcDetails}
          setEditableField={setEditableField}
          setShowSingleFieldEditOptions={setShowSingleFieldEditOptions}
          showSingleFieldEditOptions={showSingleFieldEditOptions}
          handleVerifiedChange={handleVerifiedChange}
          rejectionReasons={qcRejectionReasons}
          verifiedOptions={verifiedOptions}
          docTypes={qcDocTypes}
          qcDetails={qcDetails}
          handleRejectionReasonChange={handleRejectionReasonChange}
          handleDocTypeChange={handleDocTypeChange}
          photoVerificationErrors={photoVerificationErrors}
          getPhotoVerificationError={getPhotoVerificationError}
          setPhotoVerificationError={setPhotoVerificationError}
          removePhotoVerificationError={removePhotoVerificationError}
          isFinalApprovalModalOpen={isFinalApprovalModalOpen}
          documentDetailsSteps={documentDetailsSteps}
          setIsFinalApprovalModalOpen={setIsFinalApprovalModalOpen}
          qcApproved={qcApproved}
          permission={qcWriteRole}
          handleSaveClick={handleSaveClick}
          photoVerificationSteps={photoVerificationSteps}
        />
      )}
      {errors.length > 0 && (
        <ErrorModal
          handleSubmit={handleErrorModalSubmit}
          heading="Error while saving"
          errors={errors}
        />
      )}
    </MainPageWrapper>,
  ];
};

export const Navigation = styled.div`
  display: flex;
  width: 100%;
  gap: 10px;

  .fe-id-input {
    width: 220px;
  }
`;

export const DocImg = styled.img`
  width: 200px;
  height: 200px;
  margin-bottom: 20px;
  border-radius: 8px;
  border: 1px solid ${variables.grey200};
  object-fit: contain;
  background-color: ${variables.grey200};
  justify-content: center;
  cursor: ${({ cursor = "pointer" }) => cursor};
`;

function mapStateToProps(state) {
  const { QcDetails } = state;

  return {
    qcDetails: QcDetails.qcDetails,
    currentActiveFeId: QcDetails.currentActiveFeId,
    qcRejectionReasons: QcDetails.qcRejectionReasons,
    qcDocTypes: QcDetails.qcDocTypes,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    getQcDetails: bindActionCreators(getQcDetails, dispatch),
    getQcAdditionalData: bindActionCreators(getQcAdditionalData, dispatch),
    saveQcDetails: bindActionCreators(saveQcDetails, dispatch),
    approveQcDetails: bindActionCreators(approveQcDetails, dispatch),
    setQcDetails: bindActionCreators(setQcDetails, dispatch),
    resetState: bindActionCreators(resetState, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(QcDetails);
